import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, FormGroup } from "react-bootstrap";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { logAction } from "../../../../app/appActions.js";
import { getAvailableCountries } from "../../../../app/appSelectors";
import { BinIcon } from "../../../../components/icon/Icon.jsx";
import useUpdateEffect from "../../../../hooks/useUpdateEffect";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext.js";
import amountFormat from "../../../../utils/amountFormat";
import BonusWithdrawPanel from "../../../Bonus/components/BonusWithdrawPanel/BonusWithdrawPanel";
import {
    CashierContentContainerStyled,
    TransactionInformationContainerStyled,
    TransactionInformationIconWrapperStyled,
    TransactionInformationStyled
} from "../../../Cashier/styled/CashierStyled";
import { useGTMEvent } from "../../../GoogleTagManager/googleTagManagerHooks";
import {
    getPayoutParamsAction,
    payoutCreateAction,
    payoutGetAvailableMethodsAction,
    payoutGetFormAction
} from "../../accountActions";
import { WITHDRAWAL_AMOUNT_FIELD_ERRORS } from "../../accountConstants";
import {
    getPayoutParams,
    makeSelectPayoutAvailableMethods,
    makeSelectPayoutError,
    makeSelectPayoutForm
} from "../../accountSelectors";
import AmountField from "../AmountField";
import BillingNotAvailable from "../BillingNotAvailable";
import ChangePayment from "../ChangePayment/ChangePayment";
import PaymentForm from "../PaymentForm.jsx";
import { ChangePaymentSubmitButtonContainerStyled } from "../ChangePayment/styled/ChangePaymentStyled";
import _ from "lodash";
import { Loader } from "../../../../components/Loader/Loader";
import { isEmailValid } from "../../../../utils/escapeUtils.js";
import WithdrawDescription from "./WithdrawDescription";

function Withdraw(props) {
    const {getLangPath} = useLangContext();
    const {
        isSelectMethodSlider,
        payoutParams,
        getPayoutParams,
        payoutAvailableMethods,
        payoutCreate,
        payoutCreateCallback,
        payoutGetAvailableMethods,
        getPayoutForm,
        onDepositNowButtonClick,
        onVerifyButtonClick,
        payoutError,
        payoutForm,
        logAction,
        withdrawalAmount,
    } = props;

    const formRef = useRef();
    const [amount, setAmount] = useState(0);
    const [formError, setFormError] = useState({});
    const [selectedPayment, setSelectedPayment] = useState(null);
    const valuesRef = useRef();
    const {handleGTMClick, pushGtmEvent} = useGTMEvent({module: 'Cashier'});

    /**
     * Эффект получения параметров вывода
     */
    useEffect(() => {
        getPayoutParams();
    }, [getPayoutParams]);

    /**
     * Эффект обновления формы после выбора метода
     */
    useEffect(() => {
        if (selectedPayment) {
            getPayoutForm({
                payment: selectedPayment.id
            });
        }
    }, [selectedPayment, getPayoutForm]);

    useUpdateEffect(() => {
        if (payoutError) {
            pushGtmEvent({
                event: 'errorFillingWithdraw',
                element: WITHDRAWAL_AMOUNT_FIELD_ERRORS.includes(payoutError) ? 'amountField' : 'formError',
                error: payoutError,
                amount,
            })
        }
    }, [payoutError])

    const onChangeFieldHandler = useCallback((field, value) => {
        setFormError(prevState => {
            const newState = {
                ...prevState,
            };
            delete newState[field];
            return newState;
        })
    }, []);

    const setAmountHandler = useCallback((amount) => {
        amount = Math.min(parseInt(amount) || 0, 100000);
        setAmount(amount);
    }, []);

    const validateForm = useCallback(() => {
        const errors = payoutForm.reduce((acc, cur) => {
            const value = valuesRef.current?.[cur.field];
            switch (cur.field) {
                case 'email':
                    if (!isEmailValid(value)) {
                        acc[cur.field] = 'error.email_not_valid';
                    }
                    break;
                default:
            }
            if (cur.input_pattern) {
                if (!new RegExp(cur.input_pattern).test(value)) {
                    acc[cur.field] = `forms.validate.${cur.input_hint}` || 'forms.validate.common';
                }
            }
            return acc;
        }, {});
        if (Object.keys(errors).length === 0) {
            logAction({
                modal: 'cashier',
                tab: 'withdraw',
                operation: 'validate',
                valid: true,
            });
            return true;
        }
        setFormError(errors);
        logAction({
            modal: 'cashier',
            tab: 'withdraw',
            operation: 'validate',
            valid: false,
            errors,
        });
        return false;
    }, [payoutForm, logAction]);

    const formSubmitHandler = useCallback((e) => {
        e.preventDefault();
        if (amount > 0 && validateForm()) {
            const body = {
                amount,
                payment_id: selectedPayment.id,
                payment_name: selectedPayment.label,
                form_data: payoutForm.reduce((acc, cur) => {
                    acc[cur.field] = {
                        ...cur,
                        value: valuesRef.current?.[cur.field],
                    };
                    return acc;
                }, {})
            };
            logAction({
                modal: 'cashier',
                tab: 'withdraw',
                operation: 'submit',
                ...body,
            });

            payoutCreate({body, callback: payoutCreateCallback});
            handleGTMClick({
                amount,
                button: 'withdraw make',
                provider: selectedPayment.label,
            });
        }
    }, [validateForm, handleGTMClick, amount, selectedPayment, payoutForm, logAction, payoutCreate, payoutCreateCallback]);

    const logPaymentFormHandler = useCallback((params) => {
        logAction({
            modal: 'cashier',
            tab: 'withdraw',
            operation: 'input',
            ...params,
        });
    }, [logAction]);

    if (_.isEmpty(payoutParams)) {
        return <Loader show/>
    }

    if (!payoutParams.buyer && withdrawalAmount === 0) {
        return <CashierContentContainerStyled>
            <TransactionInformationContainerStyled>
                <TransactionInformationStyled>
                    <TransactionInformationIconWrapperStyled>
                        <BinIcon name="IcBankCardArrowRight"/>
                    </TransactionInformationIconWrapperStyled>
                    <p className="mt-16">{getLangPath('deposit.no_funds')}</p>
                    <Button variant={'primary'} size={'lg'} onClick={onDepositNowButtonClick}>
                        {getLangPath('deposit.button_now')}
                    </Button>
                </TransactionInformationStyled>
            </TransactionInformationContainerStyled>
        </CashierContentContainerStyled>
    }

    if (!payoutParams.verify) {
        return <CashierContentContainerStyled>
            <TransactionInformationContainerStyled>
                <TransactionInformationStyled>
                    <TransactionInformationIconWrapperStyled>
                        <BinIcon name={'IcCheckMarkShield'}/>
                    </TransactionInformationIconWrapperStyled>
                    <h6>{getLangPath('verify.title')}</h6>
                    <p className={'mt-2'}>{getLangPath('deposit.verify_identity')}</p>
                    <Button variant={'primary'} size={'lg'} onClick={onVerifyButtonClick}>
                        {getLangPath('deposit.verify_button')}
                    </Button>
                </TransactionInformationStyled>
            </TransactionInformationContainerStyled>
        </CashierContentContainerStyled>
    }

    return <Form onSubmit={formSubmitHandler} ref={formRef} className="d-flex flex-grow-1 flex-shrink-1">
        <CashierContentContainerStyled>
            {
                payoutError === 'error.billing_not_available' &&
                <BillingNotAvailable/>
            }

            {
                (payoutError !== 'error.billing_not_available' && payoutParams.verify) &&
                <ChangePayment
                    type={'payout'}
                    extAvailableMethods={payoutAvailableMethods}
                    extGetAvailableMethods={payoutGetAvailableMethods}
                    isSelectMethodSlider={isSelectMethodSlider}
                    selectedPayment={selectedPayment}
                    setSelectedPayment={setSelectedPayment}
                    rightColumnComponent={
                        <div className="d-flex flex-column justify-content-between mt-3">
                            <div>
                                <PaymentForm formData={payoutForm}
                                             valuesRef={valuesRef}
                                             onChangeField={onChangeFieldHandler}
                                             formError={formError}
                                             log={logPaymentFormHandler}/>
                            </div>
                            <ChangePaymentSubmitButtonContainerStyled>
                                <Button variant="primary" size='md' type="submit"
                                        disabled={!(selectedPayment && amount > 0 && !Object.keys(formError).length)}
                                        style={{minWidth: "200px", height: '52px'}}>
                                    {getLangPath('withdrawal.button', {amount: amountFormat(amount || 0, 0)})}
                                </Button>
                            </ChangePaymentSubmitButtonContainerStyled>
                        </div>
                    }
                >
                    <div>
                        <AmountField
                            description={<WithdrawDescription withdrawalAmount={withdrawalAmount}/>}
                            error={WITHDRAWAL_AMOUNT_FIELD_ERRORS.includes(payoutError)
                                ? getLangPath(payoutError, {payment_name: selectedPayment?.label || ''})
                                : null}
                            handleUsdAmountChange={setAmountHandler}
                            usdAmount={amount}
                        />

                        <BonusWithdrawPanel withdrawAmount={amount} className="mt-3"/>
                        {
                            !payoutParams.hasPositions && <>
                                <Alert variant={'warning'} className="mt-3">
                                    <BinIcon name={'IcExclamationMarkTriangle'}/>
                                    <span>{getLangPath('withdrawal.warning_has_no_positions')}</span>
                                </Alert>
                            </>
                        }

                        {
                            !!payoutError && !WITHDRAWAL_AMOUNT_FIELD_ERRORS.includes(payoutError) &&
                            <FormGroup className="mt-3">
                                <div className="text-danger">{getLangPath(payoutError)}</div>
                            </FormGroup>
                        }

                    </div>
                </ChangePayment>
            }
        </CashierContentContainerStyled>
    </Form>
}

const mapStateToProps = createStructuredSelector({
    availableCountries: getAvailableCountries,
    payoutAvailableMethods: makeSelectPayoutAvailableMethods(),
    payoutError: makeSelectPayoutError(),
    payoutParams: getPayoutParams,
    payoutForm: makeSelectPayoutForm(),
});

const mapDispatchToProps = (dispatch) => ({
    payoutCreate: payload => dispatch(payoutCreateAction(payload)),
    logAction: payload => dispatch(logAction(payload)),
    payoutGetAvailableMethods: payload => dispatch(payoutGetAvailableMethodsAction(payload)),
    getPayoutParams: payload => dispatch(getPayoutParamsAction(payload)),
    getPayoutForm: payload => dispatch(payoutGetFormAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Withdraw);
